import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ArrowLeft,
  Mail,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Zap,
  AlertCircle,
  Eye,
  Archive,
  Trash2,
  Send,
  Tag,
  Flag,
  FolderOpen,
  FileEdit,
  RefreshCw,
  Video,
  Film,
  User,
  ListVideo,
  Settings,
  CalendarDays,
  CalendarPlus,
  CalendarX,
  CalendarCog,
  CalendarRange,
  Calendar,
  Database,
  Reply,
  Forward,
  MessageSquare,
  List,
  Users,
} from 'lucide-react'
import { useParams, Link } from '@tanstack/react-router'
import { useEvent, useEventHeaders, type EventAction } from '@/api'
import {
  EventStatus,
  EventActionStatus,
  ActionType,
  getEventActionStatusDisplayName,
} from '@/lib/enum-display'
import { formatDateTime } from '@/lib/date-utils'
import { ClassificationRunDetails } from '@/components/classification/run-details'
import { EventEmailHeading } from '@/components/classification/event-list-item'

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#F25022" d="M1 1h10v10H1V1z" />
      <path fill="#00A4EF" d="M13 1h10v10H13V1z" />
      <path fill="#7FBA00" d="M1 13h10v10H1V13z" />
      <path fill="#FFB900" d="M13 13h10v10H13V13z" />
    </svg>
  )
}

function ZoomIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#2D8CFF" />
      <path
        d="M6 9.5C6 8.67 6.67 8 7.5 8H13.5C14.33 8 15 8.67 15 9.5V14.5C15 15.33 14.33 16 13.5 16H7.5C6.67 16 6 15.33 6 14.5V9.5Z"
        fill="white"
      />
      <path d="M15 10.5L18 8.5V15.5L15 13.5V10.5Z" fill="white" />
    </svg>
  )
}

function SlackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#4A154B" />
      <path d="M9.2 5.2a1.8 1.8 0 0 0-3.6 0v4.1h3.6V5.2Z" fill="#36C5F0" />
      <path d="M5.2 10.1a1.8 1.8 0 1 0 0 3.6h4.1v-3.6H5.2Z" fill="#36C5F0" />
      <path d="M18.8 9.2a1.8 1.8 0 1 0 0-3.6h-4.1v3.6h4.1Z" fill="#2EB67D" />
      <path d="M13.9 5.2a1.8 1.8 0 0 0-3.6 0v4.1h3.6V5.2Z" fill="#2EB67D" />
      <path d="M14.8 18.8a1.8 1.8 0 0 0 3.6 0v-4.1h-3.6v4.1Z" fill="#ECB22E" />
      <path d="M18.8 13.9a1.8 1.8 0 1 0 0-3.6h-4.1v3.6h4.1Z" fill="#ECB22E" />
      <path d="M5.2 14.8a1.8 1.8 0 1 0 0 3.6h4.1v-3.6H5.2Z" fill="#E01E5A" />
      <path d="M10.1 18.8a1.8 1.8 0 0 0 3.6 0v-4.1h-3.6v4.1Z" fill="#E01E5A" />
    </svg>
  )
}

function HubSpotIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#FF7A59" />
      <path
        d="M16.5 10.5V8.25C17.05 7.95 17.4 7.35 17.4 6.65C17.4 5.6 16.55 4.75 15.5 4.75C14.45 4.75 13.6 5.6 13.6 6.65C13.6 7.35 13.95 7.95 14.5 8.25V10.5C13.65 10.75 12.9 11.25 12.35 11.9L7.65 8.55C7.7 8.35 7.75 8.15 7.75 7.9C7.75 6.85 6.9 6 5.85 6C4.8 6 3.95 6.85 3.95 7.9C3.95 8.95 4.8 9.8 5.85 9.8C6.15 9.8 6.4 9.7 6.65 9.6L11.25 12.85C10.95 13.45 10.75 14.1 10.75 14.8C10.75 17.05 12.55 18.85 14.8 18.85C17.05 18.85 18.85 17.05 18.85 14.8C18.85 12.75 17.35 11.05 15.4 10.65L16.5 10.5ZM14.8 16.85C13.65 16.85 12.75 15.95 12.75 14.8C12.75 13.65 13.65 12.75 14.8 12.75C15.95 12.75 16.85 13.65 16.85 14.8C16.85 15.95 15.95 16.85 14.8 16.85Z"
        fill="white"
      />
    </svg>
  )
}

function PandaDocIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#00C389" />
      <path
        d="M8 6h8c1.1 0 2 .9 2 2v8c0 1.1-.9 2-2 2H8c-1.1 0-2-.9-2-2V8c0-1.1.9-2 2-2z"
        fill="white"
      />
      <path d="M9 9h6v1.5H9V9zm0 3h6v1.5H9V12zm0 3h4v1.5H9V15z" fill="#00C389" />
    </svg>
  )
}

function QuickBooksIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#2CA01C" />
      <circle cx="9" cy="12" r="3" fill="white" />
      <circle cx="15" cy="12" r="3" fill="white" />
      <path d="M9 12v5M15 12V7" stroke="#2CA01C" strokeWidth="2" />
    </svg>
  )
}

function GoogleDriveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M8.5 3L1 15.5h7L15.5 3H8.5z" fill="#0066DA" />
      <path d="M15.5 3L8 15.5h7L22.5 3h-7z" fill="#00AC47" />
      <path d="M1 15.5L4.75 21h14.5l3.75-5.5H1z" fill="#FFBA00" />
    </svg>
  )
}

function SharePointIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#038387" />
      <circle cx="12" cy="8" r="4" fill="white" />
      <circle cx="7" cy="14" r="3" fill="white" fillOpacity="0.8" />
      <circle cx="17" cy="14" r="3" fill="white" fillOpacity="0.8" />
      <circle cx="12" cy="18" r="2.5" fill="white" fillOpacity="0.6" />
    </svg>
  )
}

function getAccountTypeIcon(accountType: string) {
  if (accountType === 'google_email') {
    return <GoogleIcon className="h-3 w-3" />
  }
  if (accountType === 'google_drive') {
    return <GoogleDriveIcon className="h-3 w-3" />
  }
  if (accountType === 'microsoft_email') {
    return <MicrosoftIcon className="h-3 w-3" />
  }
  if (accountType === 'microsoft_sharepoint') {
    return <SharePointIcon className="h-3 w-3" />
  }
  if (accountType === 'zoom') {
    return <ZoomIcon className="h-3 w-3" />
  }
  if (accountType === 'slack') {
    return <SlackIcon className="h-3 w-3" />
  }
  if (accountType === 'hubspot') {
    return <HubSpotIcon className="h-3 w-3" />
  }
  if (accountType === 'postgres') {
    return <Database className="h-3 w-3 text-[#336791]" />
  }
  if (accountType === 'pandadoc') {
    return <PandaDocIcon className="h-3 w-3" />
  }
  if (accountType === 'quickbooks') {
    return <QuickBooksIcon className="h-3 w-3" />
  }
  return <Mail className="h-3 w-3" />
}

const ACTION_STATUS_CONFIG: Record<string, { icon: React.ReactNode; bgClass: string; textClass: string }> = {
  [EventActionStatus.PENDING]: {
    icon: <Clock className="h-4 w-4" />,
    bgClass: 'bg-yellow-100 dark:bg-yellow-900/30',
    textClass: 'text-yellow-600 dark:text-yellow-400',
  },
  [EventActionStatus.IN_PROGRESS]: {
    icon: <Loader2 className="h-4 w-4 animate-spin" />,
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    textClass: 'text-blue-600 dark:text-blue-400',
  },
  [EventActionStatus.SUCCESS]: {
    icon: <CheckCircle2 className="h-4 w-4" />,
    bgClass: 'bg-green-100 dark:bg-green-900/30',
    textClass: 'text-green-600 dark:text-green-400',
  },
  [EventActionStatus.FAILED]: {
    icon: <XCircle className="h-4 w-4" />,
    bgClass: 'bg-red-100 dark:bg-red-900/30',
    textClass: 'text-red-600 dark:text-red-400',
  },
}

const ACTION_ICONS: Record<string, React.ReactNode> = {
  // Email actions
  [ActionType.MARK_READ]: <Eye className="h-4 w-4" />,
  [ActionType.MARK_UNREAD]: <Eye className="h-4 w-4" />,
  [ActionType.ARCHIVE]: <Archive className="h-4 w-4" />,
  [ActionType.DELETE]: <Trash2 className="h-4 w-4" />,
  [ActionType.SEND_RESPONSE]: <Send className="h-4 w-4" />,
  [ActionType.DRAFT_RESPONSE]: <FileEdit className="h-4 w-4" />,
  [ActionType.LABEL]: <Tag className="h-4 w-4" />,
  [ActionType.FLAG]: <Flag className="h-4 w-4" />,
  [ActionType.MOVE]: <FolderOpen className="h-4 w-4" />,
  REPLY_EMAIL: <Reply className="h-4 w-4" />,
  FORWARD_EMAIL: <Forward className="h-4 w-4" />,
  // Calendar actions
  LIST_CALENDAR_EVENTS: <CalendarRange className="h-4 w-4" />,
  GET_CALENDAR_EVENT: <CalendarDays className="h-4 w-4" />,
  CREATE_CALENDAR_EVENT: <CalendarPlus className="h-4 w-4" />,
  UPDATE_CALENDAR_EVENT: <CalendarCog className="h-4 w-4" />,
  DELETE_CALENDAR_EVENT: <CalendarX className="h-4 w-4" />,
  RESPOND_TO_CALENDAR_EVENT: <Calendar className="h-4 w-4" />,
  LIST_CALENDARS: <Calendar className="h-4 w-4" />,
  QUICK_ADD_CALENDAR_EVENT: <CalendarPlus className="h-4 w-4" />,
  // Zoom actions
  CREATE_MEETING: <Video className="h-4 w-4" />,
  DELETE_MEETING: <Trash2 className="h-4 w-4" />,
  GET_MEETING: <Video className="h-4 w-4" />,
  GET_RECORDING: <Film className="h-4 w-4" />,
  GET_ZOOM_USER: <User className="h-4 w-4" />,
  LIST_MEETINGS: <ListVideo className="h-4 w-4" />,
  LIST_RECORDINGS: <Film className="h-4 w-4" />,
  UPDATE_MEETING: <Settings className="h-4 w-4" />,
  POST_SLACK_MESSAGE: <Send className="h-4 w-4" />,
  SEND_SLACK_DM: <MessageSquare className="h-4 w-4" />,
  LIST_SLACK_CHANNELS: <List className="h-4 w-4" />,
  LIST_SLACK_USERS: <Users className="h-4 w-4" />,
}

function getActionDisplayName(toolName: string): string {
  // Extract base tool name (remove __inbox_id suffix if present)
  const baseName = toolName.split('__')[0] ?? toolName
  
  // IMPORTANT: Keep this mapping in sync with:
  // - backend/scripts/seed_mcp_servers.py (GMAIL_ACTIONS, OUTLOOK_ACTIONS)
  // - frontend/src/components/inbox/action-permissions.tsx (ACTION_ICONS)
  // - backend/app/services/event_tracking_service.py (tool_to_action mapping)
  const displayNames: Record<string, string> = {
    // Common email actions (both Gmail and Outlook)
    read_emails: 'Read Emails',
    search_emails: 'Search Emails',
    get_email: 'Get Email',
    mark_read: 'Mark as Read',
    mark_unread: 'Mark as Unread',
    archive_email: 'Archive Email',
    delete_email: 'Delete Email',
    send_email: 'Send Email',
    reply_email: 'Reply to Email',
    forward_email: 'Forward Email',
    create_draft: 'Create Draft',
    // Gmail specific
    get_thread: 'Get Thread',
    add_label: 'Add Label',
    remove_label: 'Remove Label',
    // Outlook specific
    get_conversation: 'Get Conversation',
    move_email: 'Move Email',
    flag_email: 'Flag Email',
    create_folder: 'Create Folder',
    // Attachment actions
    get_attachments: 'Get Attachments',
    download_attachment: 'Download Attachment',
    // Calendar actions
    list_calendar_events: 'List Calendar Events',
    get_calendar_event: 'Get Calendar Event',
    create_calendar_event: 'Create Calendar Event',
    update_calendar_event: 'Update Calendar Event',
    delete_calendar_event: 'Delete Calendar Event',
    respond_to_calendar_event: 'Respond to Calendar Event',
    list_calendars: 'List Calendars',
    quick_add_calendar_event: 'Quick Add Calendar Event',
    // Zoom actions
    create_meeting: 'Create Meeting',
    delete_meeting: 'Delete Meeting',
    get_meeting: 'Get Meeting',
    get_recording: 'Get Recording',
    get_zoom_user: 'Get Zoom User',
    list_meetings: 'List Meetings',
    list_recordings: 'List Recordings',
    update_meeting: 'Update Meeting',
    // Slack actions
    post_slack_message: 'Post Slack Message',
    send_slack_dm: 'Send Slack DM',
    list_slack_channels: 'List Slack Channels',
    list_slack_users: 'List Slack Users',
    // HubSpot CRM actions
    list_contacts: 'List Contacts',
    get_contact: 'Get Contact',
    create_contact: 'Create Contact',
    update_contact: 'Update Contact',
    search_contacts: 'Search Contacts',
    list_companies: 'List Companies',
    get_company: 'Get Company',
    create_company: 'Create Company',
    update_company: 'Update Company',
    search_companies: 'Search Companies',
    list_deals: 'List Deals',
    get_deal: 'Get Deal',
    create_deal: 'Create Deal',
    update_deal: 'Update Deal',
    search_deals: 'Search Deals',
    get_hubspot_account: 'Get HubSpot Account',
    list_owners: 'List Owners',
    // PostgreSQL database actions
    run_query: 'Run Query',
    list_schemas: 'List Schemas',
    list_tables: 'List Tables',
    describe_table: 'Describe Table',
    get_server_info: 'Get Server Info',
    // PandaDoc actions
    list_documents: 'List Documents',
    get_document: 'Get Document',
    create_document_from_template: 'Create Document from Template',
    send_document: 'Send Document',
    download_document: 'Download Document',
    list_templates: 'List Templates',
    // QuickBooks actions
    list_invoices: 'List Invoices',
    get_invoice: 'Get Invoice',
    create_invoice: 'Create Invoice',
    list_customers: 'List Customers',
    get_customer: 'Get Customer',
    create_customer: 'Create Customer',
    list_expenses: 'List Expenses',
    get_company_info: 'Get Company Info',
    // Google Drive actions
    list_files: 'List Files',
    search_files: 'Search Files',
    read_document: 'Read Document',
    create_document: 'Create Document',
    update_document: 'Update Document',
    share_file: 'Share File',
    // SharePoint actions
    list_sites: 'List Sites',
    search_content: 'Search Content',
    upload_file: 'Upload File',
  }
  return displayNames[baseName] ?? baseName
}

type ActionTimelineItemProps = {
  action: EventAction
  isLast: boolean
  index: number
  eventAccountUuid: string
}

function ActionTimelineItem({ action, isLast, index, eventAccountUuid }: ActionTimelineItemProps) {
  const defaultStatusConfig = ACTION_STATUS_CONFIG[EventActionStatus.PENDING]
  const statusConfig = ACTION_STATUS_CONFIG[action.status] ?? defaultStatusConfig
  const actionIcon = ACTION_ICONS[action.action_type] ?? <Zap className="h-4 w-4" />

  // Only show target account badge for cross-account actions
  const isCrossAccountAction = action.target_account && action.target_account.uuid !== eventAccountUuid

  return (
    <div
      className="relative flex gap-4"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-border" />
      )}

      {/* Icon */}
      <div className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-background ${statusConfig?.bgClass ?? ''} ${statusConfig?.textClass ?? ''} transition-transform duration-200 hover:scale-110`}>
        {actionIcon}
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold">{getActionDisplayName(action.tool_name)}</span>
          <Badge
            variant={action.status === EventActionStatus.SUCCESS ? 'default' :
                    action.status === EventActionStatus.FAILED ? 'destructive' : 'secondary'}
            className="gap-1 text-xs"
          >
            {statusConfig?.icon}
            {getEventActionStatusDisplayName(action.status)}
          </Badge>
        </div>

        {isCrossAccountAction && action.target_account && (
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            {getAccountTypeIcon(action.target_account.account_type)}
            <span>{action.target_account.display_name}</span>
          </div>
        )}

        {action.error_message && (
          <div className="mt-3 rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
            <AlertCircle className="inline h-4 w-4 mr-1.5 -mt-0.5" />
            {action.error_message}
          </div>
        )}
      </div>
    </div>
  )
}

export function EventDetailPage() {
  // Get eventId from URL - must specify route for type safety and reactivity
  const { eventId } = useParams({ from: '/_authenticated/events/$eventId' })
  const headers = useEventHeaders([eventId])
  const { data: event, isLoading, error, refetch, isFetching } = useEvent(eventId, {
    // Poll every 5s for active events as SSE fallback
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === EventStatus.PENDING || status === EventStatus.ANALYZING ? 5000 : false
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6 stagger-children">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    )
  }

  if (error || !event) {
    return (
      <div className="space-y-6">
        <Link 
          to="/events" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Events
        </Link>
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10 mb-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold">Event Not Found</h3>
            <p className="mt-1 text-sm text-muted-foreground text-center max-w-sm">
              The event you're looking for doesn't exist or you don't have access to it.
            </p>
            <Button asChild className="mt-6">
              <Link to="/events">Go Back</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const classification = event.classification
  const title =
    event.agent_summary ?? (event.error_message ? 'Couldn’t finish' : 'Processing email')
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-start gap-3">
        <Link
          to="/events"
          aria-label="Back to events"
          className="flex size-10 shrink-0 items-center justify-center rounded-full border bg-background transition-colors hover:bg-muted"
        >
          <ArrowLeft className="size-5" />
        </Link>
        <div className="sentry-block ph-no-capture min-w-0 flex-1">
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            <EventEmailHeading
              headers={headers.data ? (headers.data[eventId] ?? null) : headers.isError ? null : undefined}
              className="[&>span:last-child]:font-normal [&>span]:whitespace-normal [&>span]:break-words"
            />
          </h1>
          <p className="mt-1 break-words text-sm text-muted-foreground">
            {event.connected_account.display_name} · {formatDateTime(event.received_at ?? event.created_at)}
          </p>
          {!classification && <p className="mt-3 font-medium">{title}</p>}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => refetch()}
          disabled={isFetching}
          aria-label="Refresh"
        >
          <RefreshCw className={`size-4 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle role="heading" aria-level={2}>
            Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {classification ? (
            <ClassificationRunDetails classification={classification} />
          ) : (
            <>
              {event.agent_summary && event.error_message && (
                <p className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
                  {event.error_message}
                </p>
              )}
              {event.actions?.length ? (
                <div>
                  {event.actions.map((action, index) => (
                    <ActionTimelineItem
                      key={action.uuid}
                      action={action}
                      isLast={index === event.actions.length - 1}
                      index={index}
                      eventAccountUuid={event.connected_account.uuid}
                    />
                  ))}
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {event.status === EventStatus.PENDING || event.status === EventStatus.ANALYZING
                    ? 'Working on it.'
                    : 'Nothing was done to this email.'}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
